import {
  EnvConfig,
  LIST_MENTIONABLE_USERS,
  useCurrentWorkspace,
  useFetcher,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataFunc, Mention, MentionsInput } from 'react-mentions';
import { Text, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { AccountAvatar } from '../accountAvatar';
import './mentionsInputFieldWeb.css';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type MentionsInputFieldProps = {
  inputFieldText: string;
  setInputFieldText: (text: string) => void;
  send: () => void;
  suggestionsPosition?: 'top' | 'bottom';
  handleEnter?: boolean;
  placeholder?: string;
  setInputHeight?: (value: number) => void;
  setIsMultiline?: (value: boolean) => void;
};

export const MentionsInputFieldWeb = (props: MentionsInputFieldProps) => {
  const { t } = useTranslation();
  const {
    inputFieldText,
    setInputFieldText,
    send,
    suggestionsPosition = 'top',
    handleEnter = true,
    placeholder,
    setInputHeight,
    setIsMultiline,
  } = props;

  // portalRef is used only by the ResizeObserver for auto-grow height measurement.
  // No suggestionsPortalHost is passed to react-mentions: without a portal the
  // library uses position:absolute (not position:fixed), so the dropdown is not
  // affected by ancestor CSS transforms (e.g. SlideLayout's translateX animation)
  // which would otherwise mis-position a fixed element.
  const portalRef = useRef<HTMLDivElement>(null);

  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const fetchUsers: DataFunc = async (search, callback) => {
    try {
      const workspaceUsers = await queryClient.fetchQuery({
        queryKey: ['mentionableUsers', workspace?.id, search],
        queryFn: useFetcher(LIST_MENTIONABLE_USERS).bind(null, {
          filter: {
            workspaceId: workspace?.id,
            keyword: search,
          },
          page: {
            limit: 10,
            offset: 0,
          },
        }),
      });

      const results =
        workspaceUsers?.account?.listMentionableUsers?.items?.map((u) => ({
          id: u.id,
          display: `${u.name.first} ${u.name.last}`,
          email: u.email,
          avatar: u.avatar,
        })) ?? [];

      callback(results);
    } catch (err) {
      console.error('mention fetch failed', err);
      callback([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (!handleEnter) return;

    if (props.inputFieldText !== '' && e.code === 'Enter' && !e.shiftKey) {
      send();
      e.preventDefault();
      setInputFieldText('');
    }
  };

  // Auto-grow: observe the container height and report back to the parent so the
  // outer footer can resize. Capped at maxHeightWebTenLines (10 lines).
  useEffect(() => {
    if (!setInputHeight || !portalRef.current) return;

    // Feature-detect ResizeObserver; skip if unavailable (older browsers/test runners)
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const el = portalRef.current;

    const observer = new ResizeObserver(() => {
      const height = Math.min(
        Math.max(el.offsetHeight, singleLineHeightWeb),
        maxHeightWebTenLines
      );
      setInputHeight(height);
      if (setIsMultiline) {
        setIsMultiline(height > singleLineHeightWeb + 4);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [setInputHeight, setIsMultiline]);

  const renderMentionRow = (
    entry: any,
    search: string,
    highlightedDisplay: any,
    index: number,
    focused: boolean
  ) => {
    return (
      <div
        style={{
          ...mentionRowStyle,
          backgroundColor: focused
            ? Colors.role.inverseTint
            : Colors.role.surface,
          fontWeight: focused ? 'bold' : 'normal',
        }}
      >
        <AccountAvatar
          avatar={entry.avatar}
          mediaUrl={EnvConfig.mediaUrl}
          size={36}
        />
        <View
          style={{
            marginLeft: 16,
          }}
        >
          <Text style={styles.mentionRowName}>{highlightedDisplay}</Text>
          <Text style={styles.mentionRowEmail}>{entry.email}</Text>
        </View>
      </div>
    );
  };

  return (
    <div
      ref={portalRef}
      style={{
        width: '100%',
        height: 'auto',
        minHeight: `${singleLineHeightWeb}px`,
        overflow: 'visible',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      <MentionsInput
        value={inputFieldText}
        placeholder={placeholder || t('common.typeMessage')}
        style={getCustomStyle(suggestionsPosition)}
        onChange={(e) => {
          setInputFieldText(e.target.value);
        }}
        spellCheck={false}
        onKeyPress={handleKeyPress}
      >
        <Mention
          trigger="@"
          markup="@[__display__](__id__)"
          appendSpaceOnAdd
          data={fetchUsers}
          renderSuggestion={renderMentionRow}
          displayTransform={(id, display) => `@${display}`}
          style={mentionStyle}
        />
      </MentionsInput>
    </div>
  );
};

const styles = themedStyleSheet((role) => ({
  mentionRowName: {
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.25,
    color: role.textPrimary,
  },
  mentionRowEmail: {
    fontSize: 12,
    fontWeight: '400',
    letterSpacing: 0.2,
    color: role.textSecondary,
  },
}));

const mentionRowStyle: any = {
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  height: 56,
  overflow: 'hidden',
  padding: '8px 16px',
  cursor: 'pointer',
};

const mentionStyle: any = {
  backgroundColor: Colors.role.surfaceHover,
  color: Colors.primaryDark,
  position: 'relative',
  zIndex: 2,
  fontSize: '16px',
};

const singleLineHeightWeb = 56;
// 10 lines × ~22 px line-height + 36 px top/bottom padding
const maxHeightWebTenLines = 256;

const getCustomStyle = (
  suggestionsPosition: 'top' | 'bottom' = 'top'
): any => ({
  control: {
    fontSize: '16px',
    paddingLeft: '12px',
    width: '100%',
    // Auto-grow for Crop Circle: height driven by content, capped at 10 lines.
    // overflow must NOT be 'hidden' here — the suggestions list is absolutely
    // positioned inside this control div, and overflow:hidden would clip it.
    // The height cap is enforced on the highlighter layer instead (see below).
    height: 'auto',
    minHeight: `${singleLineHeightWeb}px`,
    position: 'relative',
    fontFamily: 'Inter, Helvetica Neue, Helvetica, Arial, sans-serif',
    fontWeight: '400',
    color: Colors.role.textStrong,
  },
  highlighter: {
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    width: '100%',
    // Auto-grow for Crop Circle: no fixed height so content determines size.
    // maxHeight (not control.overflow) caps the text growth; overflow:hidden
    // on the highlighter is safe because the dropdown is a sibling, not a child.
    height: 'auto',
    minHeight: `${singleLineHeightWeb}px`,
    maxHeight: `${maxHeightWebTenLines}px`,
    paddingTop: '18px',
    paddingBottom: '18px',
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  input: {
    // Do not set an explicit height for crop circle — the library already applies
    // position:absolute + top:0 + bottom:0 to the multiline textarea, so it
    // stretches to fill the control without needing height:100%.  Leaving height
    // unset avoids the Firefox ambiguity where height:100% inside a height:auto
    // parent resolves to 'auto' and then competes with bottom:0.
    height: undefined,
    minHeight: `${singleLineHeightWeb}px`,
    paddingLeft: '12px',
    paddingTop: '18px',
    paddingBottom: '18px',
    width: '100%',
    fontSize: '16px',
    outline: 'none',
    border: 'none',
    overflowY: 'auto',
    overflowX: 'hidden',
    className: 'mentions-input-textarea',
    backgroundColor: 'transparent',
    borderRadius: undefined,
    boxSizing: 'border-box',
    resize: 'none',
    color: Colors.role.textStrong,
  },
  suggestions: {
    zIndex: 99999,
    list: {
      flex: 1,
      position: 'absolute',
      overflow: 'auto',
      top: suggestionsPosition === 'bottom' ? '100%' : 'auto',
      // 'calc(100% + 15px)' positions the list's bottom edge 15px above the top
      // of the control div, so the dropdown clears the input text with a small gap.
      bottom: suggestionsPosition === 'top' ? 'calc(100% + 15px)' : 'auto',
      width: 320,
      maxHeight: 280,
      minHeight: 56,
      borderRadius: 4,
      backgroundColor: Colors.role.surface,
      left: -10,
      zIndex: 99999,
      boxShadow:
        suggestionsPosition === 'bottom'
          ? '0 4px 8px rgba(0, 0, 0, 0.2)'
          : '0 -4px 8px rgba(0, 0, 0, 0.2)',
    },
  },
});
